import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import {
  IAttempt,
  IBoardConfig,
  ICalendarSet,
  ICalendarSetListItem,
} from "@miplatform/types";
import { bugsnag, deepCopy } from "@miplatform/utils";
import {
  CalendarSetConfigCalendarDataItem,
  CalendarSetsConfigData,
  CalendarSetsConfigDataItem,
  CalendarsConfigData,
} from "./calendarsStore.types";

type UseCalendarStoreState = {
  calendarSetConfigs: CalendarSetsConfigData;
  myCalendarSets: ICalendarSetListItem[];
};

type UseCalendarStoreFunctions = {
  setAttempts: (
    calendarSetId: string,
    calendarId: string,
    newAttempts: IAttempt[]
  ) => void;
  updateAttempt: (
    calendarSetId: string,
    calendarId: string,
    attemptData: IAttempt
  ) => void;
  setBoards: (
    calendarSetId: string,
    calendarId: string,
    boards: IBoardConfig[]
  ) => void;
  updateBoard: (
    calendarSetId: string,
    calendarId: string,
    board: IBoardConfig
  ) => void;
  setCalendarSetConfig: (config: ICalendarSet) => void;
  setMyCalendarSets: (calendarSets: ICalendarSetListItem[]) => void;
  getCalendar: (
    calendarSetId: string,
    calendarId: string
  ) => CalendarSetConfigCalendarDataItem | null;
};

const initialState: UseCalendarStoreState = {
  calendarSetConfigs: {},
  myCalendarSets: [],
};

const buildCalendarSetData = (
  calendarSet: ICalendarSet
): CalendarSetsConfigDataItem => {
  const calendarConfigs = calendarSet.calendarConfigs.reduce(
    (acc, calendarConfig) => {
      const calendarBoards = calendarConfig.boards ?? [];
      const boards = calendarBoards.reduce((acc, board) => {
        acc[board.id] = board;
        return acc;
      }, {} as Record<string, IBoardConfig>);

      acc[calendarConfig.id] = {
        config: calendarConfig,
        boards,
        attempts: {},
      };

      return acc;
    },
    {} as CalendarsConfigData
  );

  return {
    config: calendarSet,
    calendars: calendarConfigs,
  };
};

export const useCalendarStore = create<
  UseCalendarStoreState & UseCalendarStoreFunctions
>((set, get) => ({
  ...initialState,
  getCalendar: (calendarSetId, calendarId) => {
    const { calendarSetConfigs } = get();
    return calendarSetConfigs[calendarSetId]?.calendars[calendarId] ?? null;
  },
  setAttempts: (calendarSetId, calendarId, newCalendarAttempts) => {
    const { calendarSetConfigs, getCalendar } = get();

    if (!getCalendar(calendarSetId, calendarId)) {
      bugsnag.notify("No Calendar Config Stored Before Setting Attempts");
      return;
    }

    const attemptRecords = newCalendarAttempts.reduce((acc, attempt) => {
      acc[attempt.id] = attempt;
      return acc;
    }, {} as Record<string, IAttempt>);

    const newCalendarConfigs = deepCopy(calendarSetConfigs);

    newCalendarConfigs[calendarSetId].calendars[calendarId].attempts =
      attemptRecords;

    set({ calendarSetConfigs: newCalendarConfigs });
  },
  updateAttempt: (calendarSetId, calendarId, attempt) => {
    const { calendarSetConfigs, getCalendar } = get();

    if (!getCalendar(calendarSetId, calendarId)) {
      bugsnag.notify("No Calendar Config Stored Before Updating Attempt");
      return;
    }

    const newCalendarConfigs = deepCopy(calendarSetConfigs);

    newCalendarConfigs[calendarSetId].calendars[calendarId].attempts[
      attempt.id
    ] = attempt;

    set({ calendarSetConfigs: newCalendarConfigs });
  },
  setBoards: (calendarSetId, calendarId, boards) => {
    const { calendarSetConfigs, getCalendar } = get();

    if (!getCalendar(calendarSetId, calendarId)) {
      bugsnag.notify("No Calendar Config Stored Before Adding Boards");
      return;
    }

    const newCalendarConfigs = deepCopy(calendarSetConfigs);

    const boardRecords = boards.reduce((acc, board) => {
      acc[board.id] = board;

      return acc;
    }, {} as Record<string, IBoardConfig>);

    newCalendarConfigs[calendarSetId].calendars[calendarId].boards =
      boardRecords;

    set({ calendarSetConfigs: newCalendarConfigs });
  },
  updateBoard: (calendarSetId, calendarId, board) => {
    const { calendarSetConfigs } = get();

    if (!calendarSetConfigs[calendarSetId]?.calendars[calendarId]) {
      bugsnag.notify("No Calendar Config Stored Before Updating Board");
      return;
    }

    const newCalendarConfigs = deepCopy(calendarSetConfigs);

    newCalendarConfigs[calendarSetId].calendars[calendarId].boards[board.id] =
      board;

    set({ calendarSetConfigs: newCalendarConfigs });
  },
  setCalendarSetConfig: (newSetConfig) => {
    const { calendarSetConfigs } = get();

    const newConfigs = deepCopy(calendarSetConfigs);

    const calendarSetData = buildCalendarSetData(newSetConfig);

    newConfigs[newSetConfig.id] = calendarSetData;

    set({ calendarSetConfigs: newConfigs });
  },
  setMyCalendarSets: (calendarSets) => set({ myCalendarSets: calendarSets }),
}));

export const useStoredCalendar = (
  calendarSetId: string | null,
  calendarId: string | null
) => {
  const { calendarSet } = useStoredCalendarSet(calendarSetId);

  const firstCalendarId = calendarSet?.config.calendarConfigs.sort(
    (a, b) => a.displayOrder - b.displayOrder
  )[0]?.id;

  const calendarConfigData = calendarId
    ? calendarSet?.calendars[calendarId]
    : calendarSet?.calendars[firstCalendarId ?? ""];

  const setAttempts = useCalendarStore((state) => state.setAttempts);

  const setCalendarAttempts = (attempts: IAttempt[]) => {
    if (calendarId && calendarSetId) {
      setAttempts(calendarSetId, calendarId, attempts);
    }
  };

  return {
    calendarAttempts: calendarConfigData?.attempts ?? null,
    calendarConfig: calendarConfigData?.config ?? null,
    calendarBoards: calendarConfigData?.boards ?? null,
    calendarTrays: calendarConfigData?.config.trays ?? null,
    setCalendarAttempts,
  };
};

export const useStoredCalendarSet = (calendarSetId: string | null) => {
  const calendarSet = useCalendarStore(
    useShallow((state) =>
      calendarSetId ? state.calendarSetConfigs[calendarSetId] : null
    )
  );

  return {
    calendarSet,
  };
};

export const useCalendarSetType = (id: string) => {
  const calendarSetConfigs = useCalendarStore(
    (state) => state.calendarSetConfigs
  );

  const calendarSet = calendarSetConfigs[id];

  if (!calendarSet) {
    return null;
  }

  return calendarSet.config.eventType;
};
